import './ImageViewer.scss';
import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {PagesController} from '../pages-controller/PagesController';
import {TransformComponent, TransformWrapper} from 'react-zoom-pan-pinch';
import {debounce} from 'lodash';
import {computePageRotation} from '../../utils/utils';
import {ViewerToolbar} from '../pdf-viewer/ViewerToolbar';

interface ImageViewerProps {
    imageLink?: string;
}

export const ImageViewer = memo(({imageLink}: ImageViewerProps) => {
    const [percentage, setPercentage] = useState<number>(100);
    const [rotation, setRotation] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const debounceWaitingTime = 75;

    const setPercentageDebounced = useMemo(
        () => debounce((scale: number) => {
            setPercentage(Math.abs(100 * scale));
        }, debounceWaitingTime),
        [setPercentage]
    );

    const drawRotatedImage = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx || !imageRef.current) {
            return;
        }
        const img = imageRef.current;
        const angleInRadians = rotation * Math.PI / 180;
        const sin = Math.abs(Math.sin(angleInRadians));
        const cos = Math.abs(Math.cos(angleInRadians));

        canvas.width = img.height * sin + img.width * cos;
        canvas.height = img.height * cos + img.width * sin;

        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2); // Move origins to the center
        ctx.rotate(angleInRadians);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        ctx.restore();

        setIsLoading(false);
    }, [rotation]);


    useEffect(() => {
        if (imageLink) {
            setIsLoading(true);
            const img = new Image();
            img.onload = () => {
                imageRef.current = img;
                drawRotatedImage();
            };
            img.onerror = () => {
                setIsLoading(false);
            };
            img.src = imageLink;
        }
    }, [imageLink, drawRotatedImage]);

    useEffect(() => {
        drawRotatedImage();
    }, [rotation, drawRotatedImage]);

    useEffect(() => {
        // Cleanup function to cancel any pending debounced calls
        return () => {
            setPercentageDebounced.cancel();
        };
    }, [setPercentageDebounced]);


    const handleRotation = useCallback((rotation: number) => {
        setRotation((prevRotation) => computePageRotation(prevRotation, rotation));
    }, []);

    const handleTransformed = useCallback((_, event) => {
        setPercentageDebounced(event.scale);
    }, [setPercentageDebounced]);

    return (<div id={'HibuImagePreviewWrapper'}>
            <div className={'viewer-toolbar-container'}>
                <ViewerToolbar/>
            </div>
            <TransformWrapper
                wheel={{disabled: true}}
                doubleClick={{disabled: true}}
                limitToBounds={true}
                onTransformed={handleTransformed}
                panning={{activationKeys: ['Control', 'Alt']}}
            >
                {({zoomIn, zoomOut, resetTransform, ...rest}) => (
                    <div className="transform-wrapper">
                        <TransformComponent
                            wrapperClass="transform-component-wrapper"
                            contentClass="transform-component-content"
                        >
                            <div className="image-container">
                                <canvas ref={canvasRef} id="HibuImagePreview" className={isLoading ? '' : 'image-loaded'}/>
                            </div>

                        </TransformComponent>
                        {isLoading ?
                            <></> :
                            (<PagesController
                            showPagesButtons={false}
                            percentage={percentage}
                            handleZoomIn={zoomIn}
                            handleZoomOut={zoomOut}
                            handleResetZoomAndPan={resetTransform}
                            handlePageRotation={handleRotation}
                        />)}
                    </div>
                )}
            </TransformWrapper>
        </div>);
});
